import React from 'react';
import { ExternalLink } from 'lucide-react';
import { PrintLabelsButton } from './PrintLabelsButton';
import { toPublicLabelUrl } from '../../../../../utils/s3Utils';
import { formatCourier } from '../../../../../features/deliveries/deliveriesUtils';
import { labelResultUrls } from '../../../../../features/fulfillment/fulfillmentUtils';

/**
 * What the courier answered: who carries the shipment, its tracking, and one
 * label per parcel with a button that prints them all at once.
 *
 * Shown in the dialog that opens when the answer lands and on the exit step of
 * Preparar, so it is not lost once the dialog closes. The order's detail lays
 * the same facts out in its own places instead (the courier and the master
 * tracking among the order's facts, the labels in the parcels table). It reads
 * the one shape `courierSummaryOf` builds from the order and its parcels, which
 * is also the shape the socket event has.
 *
 * @param {object} props
 * @param {{courier: string|null, tracking_number: string|null, url_tracking: string|null,
 *   labels: Array<{item_index: number, tracking_number?: string|null, url: string|null}>}} props.summary
 * @param {string} props.testId - Prefix: `-courier`, `-tracking`, `-row`, `-print`.
 */
export const CourierLabels = ({ summary, testId }) => {
  const labels = summary?.labels || [];
  const urls = labelResultUrls(summary);

  return (
    <div data-testid={testId} className="space-y-4">
      <dl className="grid grid-cols-2 gap-4 text-sm">
        <div>
          <dt className="text-xs font-semibold text-text-muted uppercase tracking-wider">Courier</dt>
          <dd data-testid={`${testId}-courier`} className="font-semibold text-ninja-dark dark:text-white">
            {formatCourier(summary?.courier)}
          </dd>
        </div>
        <div>
          <dt className="text-xs font-semibold text-text-muted uppercase tracking-wider">Seguimiento</dt>
          <dd data-testid={`${testId}-tracking`} className="font-mono text-ninja-dark dark:text-white">
            {summary?.url_tracking ? (
              <a
                href={summary.url_tracking}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-ninja-green hover:underline"
              >
                {summary.tracking_number || 'Ver en el courier'}
                <ExternalLink className="w-3.5 h-3.5" />
              </a>
            ) : (
              summary?.tracking_number || '—'
            )}
          </dd>
        </div>
      </dl>

      <div>
        <span className="text-xs font-semibold text-text-muted uppercase tracking-wider">Etiquetas</span>
        {labels.length === 0 ? (
          <p className="mt-2 text-sm text-text-muted">
            Las etiquetas siguen generándose. Aparecen acá cuando terminen.
          </p>
        ) : (
          <ul className="mt-2 divide-y divide-border rounded-xl border border-border">
            {labels.map((label) => (
              <li
                key={label.item_index}
                data-testid={`${testId}-row`}
                className="flex items-center justify-between gap-3 px-4 py-2.5 text-sm"
              >
                <span className="text-ninja-dark dark:text-white">
                  Bulto {label.item_index}
                  {label.tracking_number && (
                    <span className="ml-2 font-mono text-xs text-text-muted">{label.tracking_number}</span>
                  )}
                </span>
                {label.url ? (
                  <a
                    href={toPublicLabelUrl(label.url)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-ninja-green hover:underline"
                  >
                    Ver etiqueta
                    <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                ) : (
                  <span className="text-xs text-text-muted">Sin etiqueta todavía</span>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      <PrintLabelsButton urls={urls} testId={`${testId}-print`} />
    </div>
  );
};
